import React from "react";
import { BubbleMatrix } from "@/features/investors/components/charts/BubbleMatrix";
import { HBars } from "@/features/investors/components/charts/HBars";
import { CHART_COLORS } from "@/features/investors/components/charts/palette";
import {
    CardHeader,
    InsightHtml,
    SectionHead,
} from "@/features/investors/components/atoms/InvestorsAtoms";
import { CorridorRow, InvestorsDashboardData } from "@/features/investors/types/investors.types";
import { InvestorsInsights } from "@/features/investors/utils/investors-insights";
import { investorsFormatters as fmt } from "@/features/investors/utils/investors-formatters";

const reviewColor = (corridor: CorridorRow): string => {
    if (corridor.reviewRatePct === null) return CHART_COLORS.teal;
    if (corridor.reviewRatePct > 7) return CHART_COLORS.coral;
    if (corridor.reviewRatePct > 4.5) return CHART_COLORS.orange;
    return CHART_COLORS.teal;
};

type Props = {
    data: InvestorsDashboardData;
    insights: InvestorsInsights;
};

export const CorridorsSection: React.FC<Props> = ({ data, insights }) => {
    const corridors = data.corridors.top;
    const maxAccounts = Math.max(...corridors.map((corridor) => corridor.activeAccounts), 1);
    const ranked = [...corridors].sort((a, b) => b.revenue - a.revenue);
    const margins = corridors
        .map((corridor) => corridor.marginPct)
        .filter((margin): margin is number => margin !== null);
    const medianMargin =
        margins.length > 0 ? [...margins].sort((a, b) => a - b)[Math.floor(margins.length / 2)] : null;

    return (
        <section className="sec" id="sec-corridors">
            <SectionHead
                index="05"
                title="Market & corridor performance"
                question={
                    <>
                        Question: <em>Where is growth happening?</em>
                    </>
                }
            />
            <div className="grid" style={{ gridTemplateColumns: "1.4fr 1fr" }}>
                <div className="card">
                    <CardHeader
                        title="Corridor opportunity matrix"
                        sub="GMV × margin · bubble = active accounts · color = review rate"
                    />
                    <BubbleMatrix
                        points={corridors.map((corridor) => ({
                            x: corridor.gmv,
                            y: corridor.marginPct ?? 0,
                            r: 8 + (corridor.activeAccounts / maxAccounts) * 26,
                            color: reviewColor(corridor),
                            label: corridor.corridor,
                            tip: `<b>${corridor.corridor}</b><br>GMV ${fmt.moneyCompact(corridor.gmv)} · Margin ${corridor.marginPct !== null ? fmt.pct(corridor.marginPct) : "—"}<br>${fmt.num(corridor.activeAccounts)} accounts · ${corridor.reviewRatePct !== null ? fmt.pct(corridor.reviewRatePct) : "—"} review rate`,
                        }))}
                        xLabel="GMV"
                        yLabel="Gross margin"
                        formatX={fmt.moneyCompact}
                    />
                    <div className="chart-foot">
                        <span className="ck">
                            <span className="ck-dot" style={{ background: CHART_COLORS.teal }} />
                            Low review (&lt;4.5%)
                        </span>
                        <span className="ck">
                            <span className="ck-dot" style={{ background: CHART_COLORS.orange }} />
                            Moderate
                        </span>
                        <span className="ck">
                            <span className="ck-dot" style={{ background: CHART_COLORS.coral }} />
                            High review (&gt;7%)
                        </span>
                    </div>
                </div>
                <div className="card">
                    <CardHeader title="Revenue by corridor" sub={`Top corridors, ${data.period.label}`} />
                    <HBars
                        items={ranked.map((corridor) => ({
                            label: corridor.corridor,
                            value: corridor.revenue,
                            color:
                                medianMargin !== null &&
                                corridor.marginPct !== null &&
                                corridor.marginPct >= medianMargin
                                    ? CHART_COLORS.teal
                                    : CHART_COLORS.orange,
                            sub: `${corridor.marginPct !== null ? fmt.pct(corridor.marginPct) : "—"} margin · ${fmt.num(corridor.operations)} ops · p90 ${corridor.p90DurationMs !== null ? fmt.durationMs(corridor.p90DurationMs) : "—"}`,
                        }))}
                        formatValue={fmt.moneyCompact}
                    />
                    <div className="chart-foot" style={{ border: 0 }}>
                        <span className="ck" style={{ color: "var(--sub)", fontSize: 11.5 }}>
                            Green = at/above the median corridor margin
                        </span>
                    </div>
                </div>
            </div>
            <div style={{ marginTop: 20 }}>
                <InsightHtml html={insights.notes.corridors} />
            </div>
        </section>
    );
};
